import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChildren,
  ChangeDetectionStrategy
} from '@angular/core';

import { MdwListItem } from '@spaces-ui/meadow/components/list-item';

export interface TabItem {
  label: string;
  alias: string;
  icon?: string;
}

@Component({
  selector: 'mdw-tab-group',
  templateUrl: './tab-group.html',
  styleUrls: ['./tab-group.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MdwListItem],
})
export class MdwTabGroup {
  private readonly destroyRef = inject(DestroyRef);

  public tabs = input.required<TabItem[]>();
  public selectedTab = input.required<string>();
  public orientation = input<'horizontal' | 'vertical'>('horizontal');

  public goTo = output<string>();

  public listItemComponents = viewChildren(MdwListItem, { read: ElementRef<HTMLElement> });

  public viewInitialized = signal(false);

  // Bumped by the ResizeObserver to invalidate the cached measurements
  // (offset / client dims) whenever the selected item or its layout changes.
  private measureTick = signal(0);

  public selectedListItem = computed(() => {
    const listItems = this.listItemComponents();
    const selectedId = this.selectedTab();
    const viewInitialized = this.viewInitialized();

    if (!viewInitialized) {
      return undefined;
    }

    const selectedElementRef = listItems.find(listItem => {
      return (listItem.nativeElement as HTMLElement).id === selectedId;
    });

    return selectedElementRef?.nativeElement as HTMLElement | undefined;
  });

  public offsetLeft = computed(() => {
    this.measureTick();
    return this.selectedListItem()?.offsetLeft;
  });

  public offsetTop = computed(() => {
    this.measureTick();
    return this.selectedListItem()?.offsetTop;
  });

  public clientWidth = computed(() => {
    this.measureTick();
    return this.selectedListItem()?.clientWidth;
  });

  public clientHeight = computed(() => {
    this.measureTick();
    return this.selectedListItem()?.clientHeight;
  });

  private resizeObserver: ResizeObserver | null = null;

  public constructor() {
    afterNextRender(() => {
      this.viewInitialized.set(true);
    });

    effect(() => {
      const selected = this.selectedListItem();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;

      if (!selected) {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.measureTick.update(t => t + 1);
      });
      this.resizeObserver.observe(selected);
    });

    this.destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    });
  }
}
